import { ReloadOutlined } from "@ant-design/icons";
import { Button, Modal, Radio, Space, Tag, Typography } from "antd";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useBreakpointView } from "../../hooks/ui-hook";
import type { AppointmentRow } from "./appointmentRows";

const { Text } = Typography;

// Quiet machinery copy: informational only — no alarm language, no error
// states for readiness, anywhere.
const PREPARING_COPY = "Preparing transfer…";
// Whisper/inline text: ≥12.5px at ≥4.5:1 contrast (#595959 on white is ~7:1).
const QUIET_TEXT_STYLE: React.CSSProperties = { fontSize: 13, color: "#595959" };
// Rows double as touch targets on phones — keep them ≥44px tall.
const ROW_MIN_HEIGHT = 44;
// Under this width the modal becomes a full-width, top-anchored sheet.
const SHEET_BREAKPOINT_PX = 480;

interface AppointmentSelectionModalProps {
  visible: boolean;
  appointments: AppointmentRow[];
  onSelect: (appointmentId: string) => void;
  onCancel: () => void;
  loading: boolean;
  title?: string;
  description?: string;
  // Execute-gate: while true, only the confirm button is held back (the list
  // stays usable). The 15s cap lives in useTransferReadinessGate, not here.
  confirmGated?: boolean;
  // When a background refresh last changed the list — drives the
  // "Updated just now" whisper.
  refreshedAt?: number | null;
  // Shown in the empty state so the clinician can sanity-check which chart
  // the (empty) schedule was looked up for.
  patientMrn?: string | null;
  // Empty-state retry affordance: re-request the appointment list.
  onRetryFetch?: () => void;
}

const AppointmentSelectionModal: React.FC<AppointmentSelectionModalProps> = ({
  visible,
  appointments,
  onSelect,
  onCancel,
  loading,
  title,
  description,
  confirmGated = false,
  refreshedAt = null,
  patientMrn,
  onRetryFetch,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const isSheet = useBreakpointView(SHEET_BREAKPOINT_PX);

  // If a refresh flags the selected row "no longer on schedule", drop the
  // selection — confirming into a removed appointment is a wrong-chart write.
  useEffect(() => {
    if (selectedId && appointments.some((row) => row.removed && row.option.id === selectedId)) {
      setSelectedId(null);
    }
  }, [appointments, selectedId]);

  const handleOk = () => {
    if (selectedId) {
      onSelect(selectedId);
    }
  };

  // Arrow-key navigation over the selectable rows. antd's Radio.Group inputs
  // share no `name`, so the browser provides no native radio roving — we
  // implement it and preventDefault to stay the single mover.
  const handleArrowNav = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
    const backward = e.key === "ArrowUp" || e.key === "ArrowLeft";
    if (!forward && !backward) return;
    const selectableIds = appointments.filter((row) => !row.removed).map((row) => row.option.id);
    if (!selectableIds.length) return;
    e.preventDefault();
    const currentIdx = selectedId ? selectableIds.indexOf(selectedId) : -1;
    const nextIdx =
      currentIdx === -1
        ? forward
          ? 0
          : selectableIds.length - 1
        : (currentIdx + (forward ? 1 : -1) + selectableIds.length) % selectableIds.length;
    setSelectedId(selectableIds[nextIdx]);
    // Move focus with the selection (roving). Enabled inputs render in row
    // order, so the nth enabled input is the nth selectable id.
    const inputs = listRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]:not([disabled])');
    inputs?.[nextIdx]?.focus();
  };

  return (
    <Modal
      title={title || "Select Appointment"}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Select"
      cancelText="Cancel"
      okButtonProps={{ disabled: !selectedId || confirmGated, loading }}
      cancelButtonProps={{ disabled: loading }}
      // Full-width top-anchored sheet on phones.
      width={isSheet ? "100%" : 600}
      style={isSheet ? { top: 0, margin: 0, maxWidth: "100vw", paddingBottom: 0 } : undefined}
      destroyOnClose={true}
      maskClosable={false}
      // Escape stays enabled while picking; disabled only once the write is
      // in flight.
      keyboard={!loading}
      zIndex={1000}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
          {/* aria-live so the gate line is announced politely; rendered even
              when empty so the live region exists before content changes. */}
          <span aria-live="polite" style={{ ...QUIET_TEXT_STYLE, marginRight: "auto", textAlign: "left" }}>
            {confirmGated ? PREPARING_COPY : ""}
          </span>
          <CancelBtn />
          <OkBtn />
        </div>
      )}
    >
      <div style={{ marginBottom: 8 }}>
        <Text>
          {description ||
            "Multiple appointments were found for this patient. Please select the appointment you want to transfer the note to:"}
        </Text>
      </div>

      {/* The only global refresh signal: a hairline whisper. */}
      <div aria-live="polite" style={{ minHeight: 18, marginBottom: 4 }}>
        {refreshedAt != null && <Text style={QUIET_TEXT_STYLE}>Updated just now</Text>}
      </div>

      {appointments.length === 0 ? (
        <div role="status" style={{ padding: "12px 0" }}>
          <div>
            <Text>No appointments today for this patient.</Text>
          </div>
          {patientMrn && (
            <div style={{ marginTop: 4 }}>
              <Text style={QUIET_TEXT_STYLE}>MRN/Patient ID: {patientMrn}</Text>
            </div>
          )}
          {onRetryFetch && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={onRetryFetch}
              disabled={loading}
              style={{ marginTop: 12 }}
            >
              Check again
            </Button>
          )}
        </div>
      ) : (
        <div ref={listRef} onKeyDown={handleArrowNav} style={{ maxHeight: 400, overflowY: "auto" }}>
          <Radio.Group onChange={(e) => setSelectedId(e.target.value)} value={selectedId} style={{ width: "100%" }}>
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              {appointments.map(({ option: apt, added, removed }) => (
                <Radio
                  key={apt.id}
                  value={apt.id}
                  disabled={removed}
                  style={{ width: "100%", minHeight: ROW_MIN_HEIGHT, display: "flex", alignItems: "center" }}
                >
                  <div style={{ display: "flex", flexDirection: "column", paddingLeft: 8 }}>
                    <span>
                      <Text strong type={removed ? "secondary" : undefined}>
                        {/* note-template / department options carry a `name`; appointments carry a `date` */}
                        {apt.name || apt.date || "Unknown date"}
                        {apt.time ? ` at ${apt.time}` : ""}
                      </Text>
                      {added && (
                        <Tag color="green" style={{ marginLeft: 8, fontSize: 12, lineHeight: "18px" }}>
                          added
                        </Tag>
                      )}
                      {removed && (
                        <Text italic style={{ ...QUIET_TEXT_STYLE, marginLeft: 8 }}>
                          no longer on schedule
                        </Text>
                      )}
                    </span>
                    {apt.category && <Text type="secondary">{apt.category}</Text>}
                    {apt.visit_type && <Text type="secondary">{apt.visit_type}</Text>}
                    {apt.department && <Text type="secondary">Department: {apt.department}</Text>}
                    {apt.provider && <Text type="secondary">Provider: {apt.provider}</Text>}
                    {apt.location && <Text type="secondary">Location: {apt.location}</Text>}
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      )}
    </Modal>
  );
};

export default AppointmentSelectionModal;
